import * as React from "react";
import {
  Show,
  TopToolbar,
  EditButton,
  useRecordContext,
  type ShowProps,
  type Identifier,
  type RaRecord,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  type StackProps,
} from "@mui/material";
import { layout } from "./tokens";

type AppShowProps = ShowProps & {
  aside?: React.ReactNode;
  actions?: React.ReactNode;
};

export function AppShow({ children, aside, actions, ...props }: AppShowProps) {
  return (
    <Show
      {...props}
      actions={actions}
      emptyWhileLoading
      aside={
        aside ? (
          <Box
            sx={{
              width: { xs: "100%", lg: layout.show.asideWidth },
              maxWidth: "100%",
              mt: 0,
            }}
          >
            {aside}
          </Box>
        ) : undefined
      }
      sx={{
        "& .RaShow-main": {
          alignItems: "flex-start",
          justifyContent: { xs: "flex-start", lg: "center" },
          gap: layout.show.gap,
        },
        "& .RaShow-card": {
          width: { xs: "100%", lg: layout.show.contentWidth },
          maxWidth: "100%",
          flex: "0 0 auto",
          borderRadius: layout.card.radius,
          boxShadow: "none",
          backgroundImage: "none",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {children}
    </Show>
  );
}

export function AppShowActions() {
  return (
    <TopToolbar>
      <EditButton />
    </TopToolbar>
  );
}

export function AppShowCard({ children }: { children: React.ReactNode }) {
  return (
    <Card
      sx={{
        borderRadius: layout.card.radius,
      }}
    >
      <CardContent
        sx={{
          p: layout.card.padding,
          "&:last-child": { pb: layout.card.padding },
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
}

export function AppShowContent({ children, ...props }: StackProps) {
  return (
    <Stack spacing={layout.section.gap} sx={{ width: "100%" }} {...props}>
      {children}
    </Stack>
  );
}

export function AppShowSection({ children, ...props }: StackProps) {
  return (
    <Stack spacing={2.5} sx={{ width: "100%" }} {...props}>
      {children}
    </Stack>
  );
}

export function AppShowSectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: subtitle ? 0.5 : 0 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

type AppShowFieldProps = {
  label: React.ReactNode;
  value?: React.ReactNode;
  children?: React.ReactNode;
  emptyText?: React.ReactNode;
};

export function AppShowField({
  label,
  value,
  children,
  emptyText = "—",
}: AppShowFieldProps) {
  const content = children ?? value;
  const isEmpty =
    content === null ||
    content === undefined ||
    content === "" ||
    content === false;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "180px minmax(0, 1fr)" },
        columnGap: 3,
        rowGap: 0.5,
        alignItems: "start",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ pt: { sm: 0.125 } }}
      >
        {label}
      </Typography>

      <Box sx={{ minWidth: 0 }}>
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        ) : typeof content === "string" || typeof content === "number" ? (
          <Typography variant="body2">{content}</Typography>
        ) : (
          content
        )}
      </Box>
    </Box>
  );
}

type AppShowTextFieldProps<RecordType = any> = {
  label: React.ReactNode;
  source: keyof RecordType & string;
  emptyText?: React.ReactNode;
  format?: (value: any, record: RecordType) => React.ReactNode;
};

export function AppShowTextField<
  RecordType extends
    | RaRecord<Identifier>
    | Omit<RaRecord<Identifier>, "id"> = any,
>({
  label,
  source,
  emptyText = "—",
  format,
}: AppShowTextFieldProps<RecordType>) {
  const record = useRecordContext<RecordType>();
  if (!record) return null;

  const rawValue = record[source];
  const value = format ? format(rawValue, record) : rawValue;

  return <AppShowField label={label} value={value} emptyText={emptyText} />;
}

type AppShowFunctionFieldProps<RecordType = any> = {
  label: React.ReactNode;
  render: (record: RecordType) => React.ReactNode;
  emptyText?: React.ReactNode;
};

export function AppShowFunctionField<
  RecordType extends
    | RaRecord<Identifier>
    | Omit<RaRecord<Identifier>, "id"> = any,
>({ label, render, emptyText = "—" }: AppShowFunctionFieldProps<RecordType>) {
  const record = useRecordContext<RecordType>();
  if (!record) return null;

  return (
    <AppShowField label={label} emptyText={emptyText}>
      {render(record)}
    </AppShowField>
  );
}
